import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { View, StyleSheet, Pressable, Platform, ScrollView, ActionSheetIOS, useWindowDimensions } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useAuthStore, useModalStore, useChatInputStore, useToastStore, useFileStore } from '@/store';
import Switch from '@/components/Inputs/Switch';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import useTranslation from '@/hooks/useTranslation';
import useNewChatBotPicker from '@/hooks/useNewChatBotPicker';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { MAX_FILES_UPLOADED_IN_MESSAGE } from '@/constants/config';
import { uploadFilesWithPlaceholders } from '@/lib/uploadFileWithPlaceholder';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { clipboardHasImage, getClipboardImageAsFile } from '@/lib/clipboardImage';
import { formatCreditBalance, wholeCredits } from '@/lib/formatCredits';
import MacComposerToolsContent from './MacComposerToolsContent';

function formatComposerCredits(credits) {
  const n = wholeCredits(credits);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return formatCreditBalance(n);
}

const createStyles = (colors, isXS) => StyleSheet.create({
  // Shared
  container: {
    flex: isXS && Platform.OS !== 'macos' ? 1 : undefined,
    backgroundColor: colors.solid_color_primary,
  },
  scrollContent: {
    paddingBottom: 120,
  },

  // ---- Desktop: popover rows ----
  desktopContainer: {
    paddingVertical: 6,
  },
  desktopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 10,
    paddingHorizontal: 15,
    minHeight: 44,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 0.15s ease' } }),
  },
  desktopRowHovered: {
    backgroundColor: colors.solid_color_secondary,
  },
  desktopIconContainer: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopRowLabel: {
    fontSize: 14,
    color: colors.text_color_primary,
    flex: 1,
  },
  desktopDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border_color_secondary,
    marginVertical: 4,
    marginHorizontal: 12,
  },
  desktopCreditsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 10,
    paddingHorizontal: 15,
    minHeight: 44,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 0.15s ease' } }),
  },
  desktopCreditsValue: {
    fontSize: 14,
    color: colors.text_color_primary,
    flex: 1,
  },

  // ---- Mobile: full layout with descriptions ----
  section: {
    paddingTop: 5,
  },
  rowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 48,
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border_color_secondary,
  },
  rowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
    marginRight: 8,
  },
  rowCopy: {
    flex: 1,
    minWidth: 0,
  },
  rowIconContainer: {
    width: 36,
    height: 36,
    flexShrink: 0,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowTitle: {
    fontSize: 15,
    color: colors.text_color_primary,
  },
  rowSubtitle: {
    fontSize: 13,
    color: colors.text_color_secondary,
    marginTop: 1,
  },
  creditsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 10,
    paddingBottom: 15,
    marginTop: 10,
    paddingHorizontal: 15,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border_color_secondary,
  },
  creditsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  creditsValue: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text_color_primary,
  },
  creditsLabel: {
    fontSize: 13,
    color: colors.text_color_secondary,
  },
  buyButton: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: colors.accent_color_primary,
  },
  buyButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.accent_text_color,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 52,
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border_color_secondary,
  },
  toggleInfo: {
    flex: 1,
    marginRight: 12,
  },
  toggleTitle: {
    fontSize: 15,
    color: colors.text_color_primary,
  },
  toggleDescription: {
    fontSize: 13,
    color: colors.text_color_secondary,
    marginTop: 1,
  },
  divider: {
    height: 8,
    backgroundColor: colors.solid_color_secondary,
    marginVertical: 8,
  },
});

/** Desktop popover row with hover state and optional trailing element (e.g. Switch) */
function DesktopMenuRow({ icon, label, onPress, colors, styles, trailing }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      style={[styles.desktopRow, hovered && styles.desktopRowHovered]}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
    >
      <View style={styles.desktopIconContainer}>
        <Icon name={icon} size={14} color={colors.text_color_primary} />
      </View>
      <Text style={styles.desktopRowLabel}>{label}</Text>
      {trailing || null}
    </Pressable>
  );
}

function ComposerToolsMenu({
  conversationId = null,
  collectionId = null,
  onClose,
  onAddFile,
  onAddUploadingFile,
  onParameterChange,
  onPasteFiles,
  files = [],
  showBotPicker = false,
  selectedBot,
  isWorkMode = false,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const fileInputRef = useRef(null);
  const [hasClipboardImage, setHasClipboardImage] = useState(false);

  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const { uploadFile, isFileAllowed, fetchAllowedFileTypes, hasCreditsForFileUpload } = useFileStore(state => state.actions);
  const setContext = useChatInputStore(state => state.setContext);
  const requireAuth = useRequireAuth();
  const { label: botPickerLabel, openBotPicker } = useNewChatBotPicker({ selectedBot, isWorkMode });

  // User credits
  const user = useAuthStore(state => state.user);
  const hasUser = Boolean(user?.id);
  const totalAvailableCredits = user?.credits?.totalAvailable ?? 0;
  const monthlyCredits = user?.credits?.monthlyCredits ?? 0;
  const totalCredits = monthlyCredits + totalAvailableCredits;

  // Context
  const contextId = conversationId || 'new';
  const hasReachedFileLimit = files?.length >= MAX_FILES_UPLOADED_IN_MESSAGE;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (Platform.OS === 'web' || Platform.OS === 'macos') return;
      const hasImage = await clipboardHasImage();
      if (!cancelled) setHasClipboardImage(hasImage);
    })();
    return () => { cancelled = true; };
  }, []);

  // Tools state — read directly from store so toggles are reactive
  const storeSelectedParameters = useChatInputStore(state =>
    state.inputStates?.[contextId]?.selectedParameters
  );
  const currentTools = useMemo(() => {
    const tools = storeSelectedParameters?.tools;
    if (Array.isArray(tools)) return tools;
    if (tools) return [tools];
    return [];
  }, [storeSelectedParameters?.tools]);

  const isWebSearchEnabled = currentTools.includes('search-web');

  // ---- Handlers ----

  // Whether this menu is rendered inside a modal (vs a desktop dropdown popover).
  const isInModal = Platform.OS !== 'macos' && (Platform.OS !== 'web' || isXS);

  const handleOpenBotPicker = useCallback(() => {
    if (!isInModal) onClose?.();
    openBotPicker();
  }, [isInModal, onClose, openBotPicker]);

  const handleBuyCredits = useCallback(() => {
    requireAuth(
      () => {
        if (isInModal) {
          // Swap modal content in-place — the bottom sheet stays open and transitions
          // to the new modal's snap points. Avoids unreliable dismiss/present cycles.
          openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
        } else {
          onClose?.();
          openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
        }
      },
      {
        source: 'composer_buy_credits',
        title: t('chatInput.authCta.creditsTitle'),
        subtitle: t('chatInput.authCta.creditsSubtitle'),
        icon: 'coins',
      }
    );
  }, [requireAuth, isInModal, openModal, onClose, t]);

  // Toggle web search
  const handleToggleTool = useCallback((toolValue) => {
    setContext(contextId);
    const willEnable = !currentTools.includes(toolValue);
    const next = willEnable
      ? [...currentTools, toolValue]
      : currentTools.filter(v => v !== toolValue);
    // The persisted web-search preference is synced in the store's
    // setParameter, which this flows through via onParameterChange.
    onParameterChange?.('tools', next.length > 0 ? next : null);
  }, [currentTools, onParameterChange, setContext, contextId]);

  // ---- File upload helpers ----

  const processFiles = useCallback(async (pickedFiles) => {
    await uploadFilesWithPlaceholders(pickedFiles, {
      collectionId,
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile,
      onAddFile,
      t,
      extraTempFields: (file) => ({ previewFile: file }),
    });
  }, [isFileAllowed, uploadFile, addToast, onAddFile, onAddUploadingFile, collectionId, t]);

  const handlePasteClipboardImage = useCallback(async () => {
    const gated = requireAuth(() => true, {
      source: 'composer_paste_clipboard',
      title: t('chatInput.authCta.uploadTitle'),
      subtitle: t('chatInput.authCta.uploadSubtitle'),
      icon: 'cloud-arrow-up',
    });
    if (gated !== true) return;

    if (hasReachedFileLimit) {
      addToast(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
      return;
    }

    try { await fetchAllowedFileTypes(); } catch (err) {
      console.warn('[ComposerToolsMenu] Failed to fetch allowed file types; falling back to server validation:', err?.message || err);
    }
    const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      return;
    }

    setContext(contextId);
    try {
      // Read clipboard before dismissing the sheet. Android 13+ only allows
      // clipboard access in a user-gesture window; closing first can fail
      // or return a stale image after the modal loses focus.
      const file = await getClipboardImageAsFile();
      if (!file) {
        addToast(t('chatInput.files.noClipboardImage'), 'error');
        setHasClipboardImage(false);
        return;
      }
      onClose?.();
      // Native clipboard paste belongs to the mounted ChatInput, not this
      // modal. Let it own context, placeholder replacement, and polling;
      // the modal can unmount immediately after onClose on Android.
      if (typeof onPasteFiles === 'function') {
        await onPasteFiles([file]);
      } else {
        await processFiles([file]);
      }
      setHasClipboardImage(false);
    } catch (error) {
      console.error('[ComposerToolsMenu] Clipboard paste failed:', error);
      addToast(error?.message || t('chatInput.uploadFailed'), 'error');
    }
  }, [requireAuth, t, hasReachedFileLimit, addToast, onClose, onPasteFiles, processFiles, setContext, contextId, fetchAllowedFileTypes, hasCreditsForFileUpload]);

  const handleUploadFile = useCallback(async () => {
    // Gate before any state mutation / picker invocation so anonymous
    // visitors don't see the OS file-picker just to fail on upload.
    const gated = requireAuth(() => true, {
      source: 'composer_upload',
      title: t('chatInput.authCta.uploadTitle'),
      subtitle: t('chatInput.authCta.uploadSubtitle'),
      icon: 'cloud-arrow-up',
    });
    if (gated !== true) return;

    if (hasReachedFileLimit) {
      addToast(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
      return;
    }
    // Prefetch the allowlist for client-side validation. If this fails we
    // still proceed — isFileAllowed defers to server-side validation when the
    // allowlist is unavailable, rather than rejecting every file.
    try { await fetchAllowedFileTypes(); } catch (err) {
      console.warn('[ComposerToolsMenu] Failed to fetch allowed file types; falling back to server validation:', err?.message || err);
    }
    const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      return;
    }
    setContext(contextId);

    if (Platform.OS === 'web') {
      // Keep modal open so the hidden <input> stays mounted until onChange fires
      fileInputRef.current?.click();
    } else if (Platform.OS === 'ios') {
      onClose?.();
      const clipboardOption = hasClipboardImage ? t('chatInput.files.actionSheetPasteFromClipboard') : null;
      const options = [
        t('chatInput.files.actionSheetCancel'),
        t('chatInput.files.actionSheetTakePhoto'),
        t('chatInput.files.actionSheetChooseFromPhotos'),
        t('chatInput.files.actionSheetBrowseFiles'),
        ...(clipboardOption ? [clipboardOption] : []),
      ];
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: 0,
        },
        async (buttonIndex) => {
          if (buttonIndex === 1) {
            const permission = await ImagePicker.requestCameraPermissionsAsync();
            if (permission.status !== 'granted') { addToast(t('chatInput.files.cameraPermissionRequired'), 'error'); return; }
            const result = await ImagePicker.launchCameraAsync({ mediaTypes: ['images'], quality: 0.8 });
            if (result.canceled) return;
            const asset = result.assets[0];
            await processFiles([{ uri: asset.uri, name: asset.fileName || `photo_${Date.now()}.jpg`, size: asset.fileSize || 0, type: asset.mimeType || 'image/jpeg' }]);
          } else if (buttonIndex === 2) {
            const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images', 'videos'], allowsMultipleSelection: true, quality: 0.8 });
            if (result.canceled) return;
            const picked = result.assets.map(a => ({ uri: a.uri, name: a.fileName || a.uri.split('/').pop() || 'photo.jpg', size: a.fileSize || 0, type: a.mimeType || 'image/jpeg' }));
            await processFiles(picked);
          } else if (buttonIndex === 3) {
            const result = await DocumentPicker.getDocumentAsync({ multiple: true, copyToCacheDirectory: true });
            if (result.canceled) return;
            const picked = result.assets.map(a => ({ uri: a.uri, name: a.name, size: a.size, type: a.mimeType }));
            await processFiles(picked);
          } else if (clipboardOption && buttonIndex === 4) {
            await handlePasteClipboardImage();
          }
        }
      );
    } else {
      onClose?.();
      const result = await DocumentPicker.getDocumentAsync({ multiple: true, copyToCacheDirectory: true });
      if (result.canceled) return;
      const picked = result.assets.map(a => ({ uri: a.uri, name: a.name, size: a.size, type: a.mimeType }));
      await processFiles(picked);
    }
  }, [hasReachedFileLimit, addToast, fetchAllowedFileTypes, hasCreditsForFileUpload, setContext, contextId, onClose, processFiles, requireAuth, t, hasClipboardImage, handlePasteClipboardImage]);

  const handleSelectFromLibrary = useCallback(() => {
    const gated = requireAuth(() => true, {
      source: 'composer_library',
      title: t('chatInput.authCta.libraryTitle'),
      subtitle: t('chatInput.authCta.librarySubtitle'),
      icon: 'folder-open',
    });
    if (gated !== true) return;

    if (hasReachedFileLimit) {
      addToast(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
      return;
    }
    setContext(contextId);
    const filesModalProps = {
      onSelect: (picked) => {
        if (Array.isArray(picked)) {
          picked.forEach(f => onAddFile?.(f));
        } else if (picked) {
          onAddFile?.(picked);
        }
      }
    };
    if (isInModal) {
      // Swap modal content in-place — the bottom sheet stays open and transitions
      // to the new modal's snap points. Avoids unreliable dismiss/present cycles.
      openModal('FilesModal', filesModalProps);
    } else {
      onClose?.();
      openModal('FilesModal', filesModalProps);
    }
  }, [hasReachedFileLimit, addToast, setContext, contextId, isInModal, onClose, openModal, onAddFile, requireAuth, t]);

  // Web file input handler — fires after the user picks files from the browser dialog
  const handleFileInputChange = useCallback(async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) {
      // User cancelled the dialog
      return;
    }
    const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setContext(contextId);
    // Close the menu now that we have files to process
    onClose?.();
    await uploadFilesWithPlaceholders(selectedFiles, {
      collectionId,
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile,
      onAddFile,
      t,
      extraTempFields: (file) => ({ previewFile: file }),
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [hasCreditsForFileUpload, addToast, setContext, contextId, onClose, isFileAllowed, uploadFile, onAddFile, onAddUploadingFile, collectionId, t]);

  // ---- Desktop: polished popover with icons + toggles ----
  const desktopContent = (
    <View style={styles.desktopContainer}>
      {/* Credits */}
      <DesktopMenuRow
        icon="coins"
        label={`${formatComposerCredits(totalCredits)} ${t('chatInput.credits.label')}`}
        onPress={handleBuyCredits}
        colors={colors}
        styles={styles}
        trailing={<Icon name="chevron-right" size={12} color={colors.text_color_tertiary} />}
      />

      <View style={styles.desktopDivider} />

      {showBotPicker && (
        <DesktopMenuRow
          icon="robot"
          label={botPickerLabel}
          onPress={handleOpenBotPicker}
          colors={colors}
          styles={styles}
          trailing={<Icon name="chevron-right" size={12} color={colors.text_color_tertiary} />}
        />
      )}

      {/* Tool toggles */}
      <DesktopMenuRow
        icon="earth-americas"
        label={t('chatInput.tools.webSearch')}
        onPress={() => handleToggleTool('search-web')}
        colors={colors}
        styles={styles}
        trailing={
          <Switch
            value={isWebSearchEnabled}
            onValueChange={() => handleToggleTool('search-web')}
          />
        }
      />

      {/* File actions — account-bound (library + uploads), hidden for
          logged-out visitors who have nowhere to store or read files. */}
      {hasUser && (
        <>
          <View style={styles.desktopDivider} />
          <DesktopMenuRow
            icon="folder-open"
            label={t('chatInput.files.selectFromLibrary')}
            onPress={handleSelectFromLibrary}
            colors={colors}
            styles={styles}
          />
          <DesktopMenuRow
            icon="paperclip"
            label={t('chatInput.files.addPhotosAndFiles')}
            onPress={handleUploadFile}
            colors={colors}
            styles={styles}
          />
        </>
      )}
    </View>
  );

  // ---- Mobile: full layout with descriptions ----
  const mobileContent = (
    <>
      <View style={styles.creditsRow}>
        <View style={styles.creditsLeft}>
          <Icon name="coins" size={14} color={colors.text_color_secondary} />
          <Text style={styles.creditsValue}>{formatComposerCredits(totalCredits)}</Text>
          <Text style={styles.creditsLabel}>{t('chatInput.credits.label')}</Text>
        </View>
        <Pressable accessibilityRole="button" style={styles.buyButton} onPress={handleBuyCredits}>
          <Text style={styles.buyButtonText}>{t('chatInput.credits.buyMore')}</Text>
        </Pressable>
      </View>

      {showBotPicker && (
        <View style={styles.section}>
          <Pressable accessibilityRole="button" accessibilityLabel={botPickerLabel}
            accessibilityHint="Choose, change, or clear a bot and manage your default bot."
            style={[styles.rowButton, { borderBottomWidth: 0 }]} onPress={handleOpenBotPicker}>
            <View style={styles.rowLeft}>
              <View style={styles.rowIconContainer}>
                <Icon name="robot" size={16} color={colors.text_color_primary} />
              </View>
              <View style={styles.rowCopy}>
                <Text style={styles.rowTitle}>{botPickerLabel}</Text>
              </View>
            </View>
            <Icon name="chevron-right" size={16} color={colors.text_color_tertiary} />
          </Pressable>
        </View>
      )}

      {/* File actions — account-bound (library + uploads), hidden for
          logged-out visitors who have nowhere to store or read files. */}
      {hasUser && (
        <View style={styles.section}>
          <Pressable accessibilityRole="button" accessibilityLabel={t('chatInput.files.uploadFile')} style={styles.rowButton} onPress={handleUploadFile}>
            <View style={styles.rowLeft}>
              <View style={styles.rowIconContainer}>
                <Icon name="arrow-up-from-bracket" size={16} color={colors.text_color_primary} />
              </View>
              <View style={styles.rowCopy}>
                <Text style={styles.rowTitle}>{t('chatInput.files.uploadFile')}</Text>
                <Text style={styles.rowSubtitle}>{t('chatInput.files.uploadSubtitle')}</Text>
              </View>
            </View>
            <Icon name="chevron-right" size={16} color={colors.text_color_tertiary} />
          </Pressable>

          {hasClipboardImage && Platform.OS !== 'web' && (
            <Pressable accessibilityRole="button" accessibilityLabel={t('chatInput.files.pasteFromClipboard')} style={styles.rowButton} onPress={handlePasteClipboardImage}>
              <View style={styles.rowLeft}>
                <View style={styles.rowIconContainer}>
                  <Icon name="image" size={16} color={colors.text_color_primary} />
                </View>
                <View style={styles.rowCopy}>
                  <Text style={styles.rowTitle}>{t('chatInput.files.pasteFromClipboard')}</Text>
                  <Text style={styles.rowSubtitle}>{t('chatInput.files.pasteFromClipboardSubtitle')}</Text>
                </View>
              </View>
              <Icon name="chevron-right" size={16} color={colors.text_color_tertiary} />
            </Pressable>
          )}

          <Pressable accessibilityRole="button" accessibilityLabel={t('chatInput.files.selectFromLibrary')} style={[styles.rowButton, { borderBottomWidth: 0 }]} onPress={handleSelectFromLibrary}>
            <View style={styles.rowLeft}>
              <View style={styles.rowIconContainer}>
                <Icon name="folder-open" size={16} color={colors.text_color_primary} />
              </View>
              <View style={styles.rowCopy}>
                <Text style={styles.rowTitle}>{t('chatInput.files.selectFromLibrary')}</Text>
                <Text style={styles.rowSubtitle}>{t('chatInput.files.selectFromLibrarySubtitle')}</Text>
              </View>
            </View>
            <Icon name="chevron-right" size={16} color={colors.text_color_tertiary} />
          </Pressable>
        </View>
      )}

      <View style={styles.section}>
        <View style={[styles.toggleRow, { borderBottomWidth: 0 }]}>
          <View style={styles.toggleInfo}>
            <Text style={styles.toggleTitle}>{t('chatInput.tools.webSearch')}</Text>
            <Text style={styles.toggleDescription}>{t('chatInput.tools.webSearchDescription')}</Text>
          </View>
          <Switch
            accessibilityLabel={t('chatInput.tools.webSearch')}
            value={isWebSearchEnabled}
            onValueChange={() => handleToggleTool('search-web')}
          />
        </View>
      </View>
    </>
  );

  return (
    <View style={styles.container}>
      {Platform.OS === 'macos' ? (
        <MacComposerToolsContent
          hasUser={hasUser}
          credits={formatComposerCredits(totalCredits)}
          onUpload={handleUploadFile}
          onLibrary={handleSelectFromLibrary}
          onBuyCredits={handleBuyCredits}
          onChooseBot={showBotPicker ? handleOpenBotPicker : undefined}
          botPickerLabel={botPickerLabel}
          webSearchEnabled={isWebSearchEnabled}
          onToggleWebSearch={() => handleToggleTool('search-web')}
        />
      ) : Platform.OS === 'web' && !isXS ? (
        desktopContent
      ) : Platform.OS === 'web' ? (
        <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
          {mobileContent}
        </ScrollView>
      ) : (
        <ScrollView contentContainerStyle={styles.scrollContent}>
          {mobileContent}
        </ScrollView>
      )}
      {Platform.OS === 'web' && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          multiple
        />
      )}
    </View>
  );
}

const MemoizedComposerToolsMenu = React.memo(ComposerToolsMenu);

MemoizedComposerToolsMenu.modalOptions = {
  width: 340,
  height: 340,
  heightMobile: ['45%'],
  background: 'primary',
  enableContentPanningGesture: false,
};

export default MemoizedComposerToolsMenu;

/**
 * Desktop-only dropdown wrapper that renders ComposerToolsMenu in a popover
 * anchored to the trigger button (the + button).
 */
export function ComposerToolsDropdown({
  TriggerComponent,
  anchorRef,
  triggerComponentProps = {},
  conversationId,
  collectionId,
  onAddFile,
  onAddUploadingFile,
  onParameterChange,
  files,
  showBotPicker = false,
  selectedBot,
  isWorkMode = false,
}) {
  const [open, setOpen] = React.useState(false);
  const { colors } = useTheme();
  const { height: windowHeight } = useWindowDimensions();

  const dropdownStyles = useMemo(() => StyleSheet.create({
    content: {
      width: Platform.OS === 'macos' ? 320 : 340,
      maxHeight: Math.min(440, windowHeight - 120),
      backgroundColor: colors.solid_color_primary,
      borderRadius: Platform.OS === 'macos' ? 12 : 14,
      padding: 0,
      overflow: 'hidden',
      ...Platform.select({
        web: {
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 0.5px rgba(0, 0, 0, 0.08)',
        },
        macos: {
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border_color_secondary,
          shadowColor: '#000',
          shadowOpacity: 0.18,
          shadowRadius: 18,
          shadowOffset: { width: 0, height: 6 },
        },
      }),
    },
  }), [colors, windowHeight]);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} {...(Platform.OS === 'macos' ? { anchorRef } : {})}>
      <DropdownMenu.Trigger asChild>
        <TriggerComponent {...triggerComponentProps} asDropdownTrigger />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        style={dropdownStyles.content}
        className={Platform.OS === 'web' ? 'DropdownMenuContent ComposerToolsDropdown' : undefined}
        align="start"
        side="top"
        sideOffset={8}
        avoidCollisions={true}
        collisionPadding={{ top: 60, bottom: 80, left: 15, right: 16 }}
      >
        <ComposerToolsMenu
          conversationId={conversationId}
          collectionId={collectionId}
          onClose={() => setOpen(false)}
          onAddFile={onAddFile}
          onAddUploadingFile={onAddUploadingFile}
          onParameterChange={onParameterChange}
          files={files}
          showBotPicker={showBotPicker}
          selectedBot={selectedBot}
          isWorkMode={isWorkMode}
        />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
